import { useEffect, useState } from "react";
import { Share2, Copy, Check, Loader2, Globe, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { shareConversation, unshareConversation } from "@/lib/api";

/**
 * Modal to generate / show / revoke a public share link for a conversation.
 * Triggered by the Share button in the chat header. Idempotent: re-opening
 * for an already-shared conversation just shows the existing URL.
 */
export function ShareChatModal({ open, onClose, conversation, onShareStateChange }) {
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Auto-mint on open if not yet shared.
  useEffect(() => {
    if (!open || !conversation?.id) return;
    setCopied(false);
    setShareUrl("");
    setLoading(true);
    shareConversation(conversation.id)
      .then((r) => {
        setShareUrl(r.data.share_url);
        onShareStateChange?.({ is_public: true, share_token: r.data.share_token });
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not create share link");
        onClose?.();
      })
      .finally(() => setLoading(false));
    // onClose / onShareStateChange are parent callbacks; re-running on their identity would re-share
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversation?.id]);

  if (!open) return null;

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy");
    }
  };

  const handleRevoke = async () => {
    if (!conversation?.id) return;
    setRevoking(true);
    try {
      await unshareConversation(conversation.id);
      onShareStateChange?.({ is_public: false, share_token: null });
      toast.success("Share link revoked");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke");
    } finally {
      setRevoking(false);
    }
  };

  const socials = [
    {
      name: "X",
      href: shareUrl ? `https://x.com/intent/tweet?text=${encodeURIComponent("I had a great chat with Sabzy AI — read it here:")}&url=${encodeURIComponent(shareUrl)}` : "",
    },
    {
      name: "WhatsApp",
      href: shareUrl ? `https://wa.me/?text=${encodeURIComponent(`Check out this chat with Sabzy AI: ${shareUrl}`)}` : "",
    },
    {
      name: "LinkedIn",
      href: shareUrl ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` : "",
    },
    {
      name: "Email",
      href: shareUrl ? `mailto:?subject=${encodeURIComponent("A Sabzy AI conversation")}&body=${encodeURIComponent(`Read this chat: ${shareUrl}`)}` : "",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="share-modal-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
              <Share2 className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">Share chat</div>
              <div className="text-xs text-muted-foreground">Public read-only link</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground transition hover:bg-secondary hover:text-foreground"
            aria-label="Close"
            data-testid="share-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 rounded-lg border border-border/60 bg-secondary/30 p-3 text-xs text-muted-foreground">
          <div className="flex items-start gap-2">
            <Globe className="mt-0.5 h-3.5 w-3.5 flex-none text-primary" />
            <span>
              Anyone with this link can read this conversation. They can&apos;t see your account
              or send new messages. You can revoke the link anytime.
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground" data-testid="share-loading">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating link…
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-stretch gap-2">
              <input
                value={shareUrl}
                readOnly
                onFocus={(e) => e.target.select()}
                className="min-w-0 flex-1 truncate rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono text-foreground"
                data-testid="share-url-input"
              />
              <button
                onClick={handleCopy}
                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition hover:opacity-90"
                data-testid="share-copy-btn"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
                  data-testid={`share-social-${s.name.toLowerCase()}`}
                >
                  <ExternalLink className="h-3 w-3" /> {s.name}
                </a>
              ))}
            </div>

            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="w-full rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs font-medium text-destructive transition hover:bg-destructive/10 disabled:opacity-60"
              data-testid="share-revoke-btn"
            >
              {revoking ? "Revoking…" : "Revoke link"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ShareChatModal;
